import React from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import SectionChip from '../components/SectionChip';
import { formatTimestamp } from '../utils/date-utils';
import { BTN_ROW_TEXT } from './WorkOrderBits';
import { buttonClass } from '../components/ui/Button';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Reveal from '../components/ui/Reveal';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import type { BoxItem, BoxRef, PickBox, PoolLine, PoolRequest } from './workOrdersApi';

/** Cards are touched, not clicked: their keys get a full-size hit area. */
export const TOUCH = 'min-h-[2.75rem]';

const RUN_LINK =
  'rounded font-medium text-on-surface hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

/** "2:14 PM" today, "Oct 19, 2:14 PM" before. */
function requestedWhen(at: string): string {
  const sameDay = new Date(at).toDateString() === new Date().toDateString();
  return formatTimestamp(at, sameDay ? 'h:mm A' : 'MMM D, h:mm A');
}

export const itemName = (it: Pick<BoxItem, 'display_name' | 'sku'>) => it.display_name || it.sku;

const requestedQty = (l: PoolLine) => l.requests.reduce((sum, r) => sum + (r.quantity || 0), 0);

const RequestedChip: React.FC<{ line: PoolLine }> = ({ line: l }) => {
  if (!l.requests.length) return null;
  const n = requestedQty(l);
  return (
    <Tooltip label={l.requests.length === 1 ? `The line asked for ${n} more` : `${l.requests.length} requests from the line, ${n} in all`}>
      <Tag size="sm" tone="accent" className="tabular-nums">
        Requested {n}
      </Tag>
    </Tooltip>
  );
};

/** Past this many runs a line names the first few and counts the rest. */
const RUNS_SHOWN = 3;

/** The runs behind a line's need, oldest first, each one link away. */
const RunsNeeding: React.FC<{ line: PoolLine; align?: 'right' | 'left' }> = ({ line: l, align = 'left' }) => {
  if (!l.runs.length) return null;
  const shown = l.runs.slice(0, RUNS_SHOWN);
  const rest = l.runs.length - shown.length;
  return (
    <ul
      aria-label={`Runs that need ${l.target_name}`}
      className={`space-y-0.5 text-xs text-on-surface-variant ${align === 'right' ? 'text-right' : ''}`}
    >
      {shown.map((r) => (
        <li key={r.work_order_id} className="whitespace-nowrap tabular-nums">
          {r.needed} for{' '}
          <Link to={`/work-orders/${r.work_order_id}`} className={RUN_LINK}>
            {r.number}
          </Link>
        </li>
      ))}
      {rest > 0 && (
        <li
          className="whitespace-nowrap"
          title={l.runs.slice(RUNS_SHOWN).map((r) => `${r.needed} for ${r.number}`).join('\n')}
        >
          {rest} more {rest === 1 ? 'run' : 'runs'}
        </li>
      )}
    </ul>
  );
};

export interface LinesMeta {
  /** Lines a pick just covered while the page was open: their chip lands once. */
  justCovered: ReadonlySet<string>;
  isOpen: (line: PoolLine) => boolean;
  onToggle: (line: PoolLine) => void;
  canPick: boolean;
  busy: boolean;
  onPickBox: (box: BoxRef, label: string) => void;
}

const metaOf = (m: unknown) => m as LinesMeta;

/** What the box holds that the pool asked for, then everything else in it. */
function splitBox(b: PickBox): { wanted: BoxItem[]; others: BoxItem[] } {
  const items: BoxItem[] =
    b.items && b.items.length
      ? b.items
      : [{ sku: b.sku || b.barcode, display_name: b.display_name, quantity_remaining: b.quantity_remaining, on_this_line: true }];
  const wanted = items.filter((it) => it.on_this_line);
  return { wanted: wanted.length ? wanted : items.slice(0, 1), others: items.filter((it) => !wanted.includes(it)) };
}

/** The warehouse a box sits in, falling back to the location's own name for a box on a plain shelf. */
const warehouseOf = (b: PickBox) => b.warehouse_name || b.location_name;

const since = (b: PickBox) => (b.received_at ? `in since ${formatTimestamp(b.received_at, 'MMM D')}` : null);

/** A section chip plus its warehouse, or the warehouse alone when the box has no section. */
const BoxPlace: React.FC<{ box: PickBox }> = ({ box: b }) => (
  <>
    {b.section_code && <SectionChip code={b.section_code} />}
    {warehouseOf(b) && <span className="truncate">{warehouseOf(b)}</span>}
  </>
);

const placeText = (b: PickBox) =>
  [b.section_code, b.area_name, warehouseOf(b), since(b)].filter(Boolean).join(', ');

/** The distinct sections of a line's boxes, then the warehouses of boxes that have none. */
export function lineSections(l: PoolLine): { codes: string[]; warehouses: string[] } {
  const codes: string[] = [];
  const warehouses: string[] = [];
  for (const b of l.boxes) {
    if (b.section_code) {
      if (!codes.includes(b.section_code)) codes.push(b.section_code);
    } else {
      const w = warehouseOf(b);
      if (w && !warehouses.includes(w)) warehouses.push(w);
    }
  }
  return { codes, warehouses };
}

const SectionsCell: React.FC<{ line: PoolLine }> = ({ line: l }) => {
  const { codes, warehouses } = lineSections(l);
  if (!codes.length && !warehouses.length) return <span className="text-on-surface-variant">—</span>;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-on-surface-variant">
      {codes.map((c) => (
        <SectionChip key={c} code={c} />
      ))}
      {warehouses.map((w) => (
        <span key={w} className="whitespace-nowrap">
          {w}
        </span>
      ))}
    </div>
  );
};

/** One box, its wanted part named on the barcode's own row so the open list never hides it. */
const BoxRow: React.FC<{ box: PickBox; m: LinesMeta; card?: boolean }> = ({ box: b, m, card }) => {
  const { wanted, others } = splitBox(b);
  const wantedText = wanted.map((it) => `${it.quantity_remaining} ${itemName(it)}`).join(', ');
  const place = placeText(b);
  return (
    <li className={`rounded px-2.5 py-1.5 text-xs ${card ? 'bg-surface-container-high' : 'bg-surface-container'}`}>
      <div className="flex min-w-0 items-center gap-3">
        <span className="shrink-0 font-mono text-on-surface">{b.barcode}</span>
        <span className="min-w-0 flex-1 truncate tabular-nums text-on-surface" title={wantedText}>
          {wantedText}
        </span>
        {!card && place && (
          <span className="flex min-w-0 max-w-[16rem] shrink items-center gap-1.5 text-on-surface-variant" title={place}>
            <BoxPlace box={b} />
          </span>
        )}
        {m.canPick && (
          <Tooltip label="Move the whole box to Assembly">
            <button
              type="button"
              disabled={m.busy}
              onClick={() => m.onPickBox({ barcode: b.barcode }, b.barcode)}
              className={`shrink-0 ${buttonClass('quiet', 'sm')} ${card ? TOUCH : ''}`}
              aria-label={`Pick ${b.barcode} into Assembly`}
            >
              Pick
            </button>
          </Tooltip>
        )}
      </div>
      {card && place && (
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-on-surface-variant" title={place}>
          <BoxPlace box={b} />
        </div>
      )}
      {others.length > 0 && (
        <div
          className="mt-0.5 truncate text-on-surface-variant"
          title={others.map((it) => `${it.quantity_remaining} ${itemName(it)}`).join('\n')}
        >
          Also inside: {others.map((it) => `${it.quantity_remaining} ${itemName(it)}`).join(', ')}
        </div>
      )}
    </li>
  );
};

const BoxesDisclosure: React.FC<{ line: PoolLine; m: LinesMeta; card?: boolean }> = ({ line: l, m, card }) => {
  if (l.boxes.length === 0) {
    return (
      <span className={`whitespace-nowrap text-xs ${l.to_move > 0 ? 'text-red-300' : 'text-on-surface-variant'}`}>
        None on the shelf
      </span>
    );
  }
  const open = m.isOpen(l);
  return (
    <div className="min-w-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => m.onToggle(l)}
        className={`${BTN_ROW_TEXT} -ml-2.5 ${card ? TOUCH : ''}`}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          fill="none"
          className={`h-3 w-3 transition-transform duration-150 ease-emphasized motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
        >
          <path fill="currentColor" d="M6 3.5 10.5 8 6 12.5z" />
        </svg>
        {l.boxes.length} {l.boxes.length === 1 ? 'box' : 'boxes'}
      </button>
      <Reveal show={open}>
        <ul aria-label={`Boxes holding ${l.target_name}`} className="mt-1 space-y-1 p-1">
          {l.boxes.map((b) => (
            <BoxRow key={b.barcode} box={b} m={m} card={card} />
          ))}
        </ul>
      </Reveal>
    </div>
  );
};

const PartCell: React.FC<{ line: PoolLine }> = ({ line: l }) => (
  <div className="min-w-0">
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-on-surface" title={l.target_name}>
        {l.target_name}
      </span>
      <RequestedChip line={l} />
    </div>
    {l.traced && <div className="text-xs text-on-surface-variant">Scanned onto each drone at the bench</div>}
  </div>
);

const ToMove: React.FC<{ line: PoolLine; m: LinesMeta }> = ({ line: l, m }) =>
  l.to_move > 0 ? (
    <span className="font-bold tabular-nums text-on-surface">{l.to_move}</span>
  ) : (
    <Tag size="sm" tone="good" className={m.justCovered.has(l.key) ? 'motion-safe:animate-menuIn' : ''}>
      Covered
    </Tag>
  );

const COLUMNS: ColumnDef<PoolLine, any>[] = [
  {
    id: 'target',
    header: 'Part',
    accessorFn: (l: PoolLine) => l.target_name,
    meta: { cellClass: 'max-w-[22rem]' },
    cell: ({ row }) => <PartCell line={row.original} />,
  },
  {
    id: 'needed',
    header: 'Needed',
    accessorFn: (l: PoolLine) => l.needed,
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => (
      <div>
        <div className="tabular-nums text-on-surface">{row.original.needed}</div>
        <RunsNeeding line={row.original} align="right" />
      </div>
    ),
  },
  {
    id: 'in_assembly',
    header: 'In Assembly',
    accessorFn: (l: PoolLine) => l.in_assembly,
    meta: { align: 'right', numeric: true },
  },
  {
    id: 'to_move',
    header: 'To move',
    accessorFn: (l: PoolLine) => l.to_move,
    meta: { align: 'right', numeric: true },
    cell: ({ row, table }) => <ToMove line={row.original} m={metaOf(table.options.meta)} />,
  },
  {
    id: 'sections',
    header: 'Sections',
    meta: { cellClass: 'max-w-[14rem]' },
    cell: ({ row }) => <SectionsCell line={row.original} />,
  },
  {
    id: 'boxes',
    header: 'Boxes to take',
    meta: { cellClass: 'min-w-[18rem]' },
    cell: ({ row, table }) => <BoxesDisclosure line={row.original} m={metaOf(table.options.meta)} />,
  },
];

const getKey = (l: PoolLine) => l.key;

const LineCard: React.FC<{ line: PoolLine; m: LinesMeta }> = ({ line: l, m }) => (
  <ListRow className="text-sm">
    <PartCell line={l} />
    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs tabular-nums text-on-surface-variant">
      <span>
        Needed <span className="text-on-surface">{l.needed}</span>
      </span>
      <span>
        In Assembly <span className="text-on-surface">{l.in_assembly}</span>
      </span>
      {l.to_move > 0 ? (
        <span>
          To move <span className="font-bold text-on-surface">{l.to_move}</span>
        </span>
      ) : (
        <ToMove line={l} m={m} />
      )}
    </div>
    <div className="mt-1">
      <RunsNeeding line={l} />
    </div>
    {l.boxes.length > 0 && (
      <div className="mt-1.5">
        <SectionsCell line={l} />
      </div>
    )}
    <div className="mt-1">
      <BoxesDisclosure line={l} m={m} card />
    </div>
  </ListRow>
);

export interface BandRequest extends PoolRequest {
  target_name: string;
}

/**
 * Open requests from the line, above the list. The one tinted element on the
 * page: somebody is standing at the bench with a drone set aside.
 */
export const RequestBand: React.FC<{
  requests: BandRequest[];
  canCancel: boolean;
  cancelling: string | null;
  onCancel: (r: BandRequest) => void;
  className?: string;
}> = ({ requests, canCancel, cancelling, onCancel, className = '' }) => (
  <ListStack
    label="Requested from the line"
    items={requests}
    getKey={(r) => r.id}
    on="panel"
    spacing="loose"
    className={className}
    renderRow={(r) => (
      <ListRow
        tone="accent"
        className="text-sm"
        actions={
          canCancel ? (
            <Tooltip label="Drop the request. The station can ask again.">
              <button
                type="button"
                disabled={cancelling === r.id}
                onClick={() => onCancel(r)}
                aria-label={`Cancel the request for ${r.target_name} on ${r.number}`}
                className={`${buttonClass('quiet', 'sm')} ${TOUCH} sm:min-h-0`}
              >
                {cancelling === r.id ? 'Cancelling…' : 'Cancel'}
              </button>
            </Tooltip>
          ) : undefined
        }
      >
        <span className="text-on-surface">
          {r.unit_position != null ? `Unit ${r.unit_position} on ` : ''}
          <Link to={`/work-orders/${r.work_order_id}`} className={RUN_LINK}>
            {r.number}
          </Link>{' '}
          asked for{' '}
          <span className="font-semibold tabular-nums">
            {r.quantity} {r.target_name}
          </span>
          <span className="text-on-surface-variant">
            {r.requested_by_name ? `, by ${r.requested_by_name}` : ''}, {requestedWhen(r.requested_at)}
          </span>
        </span>
      </ListRow>
    )}
  />
);

interface Props {
  lines: PoolLine[];
  meta: LinesMeta;
  wide: boolean;
  status: React.ReactNode;
  empty: React.ReactNode;
}

/** Rows per part: what the runs need, what Assembly holds, what to move and from which box. */
const PoolLines: React.FC<Props> = ({ lines, meta, wide, status, empty }) => {
  if (wide) {
    return (
      <DataTable<PoolLine> data={lines} columns={COLUMNS} meta={meta} getRowId={getKey} strip={status} empty={empty} />
    );
  }
  return (
    <div className="px-3 pb-3">
      <p className="px-1 pb-2">{status}</p>
      {lines.length === 0 ? (
        <div className="px-1">{empty}</div>
      ) : (
        <ListStack
          items={lines}
          getKey={getKey}
          on="panel"
          spacing="loose"
          renderRow={(l) => <LineCard line={l} m={meta} />}
        />
      )}
    </div>
  );
};

export default PoolLines;
